import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AlertTriangle, ArrowLeft, Shield } from "lucide-react";
import { useAuthStore } from "../stores/auth-store";
import { Button } from "../components/ui/button";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";
import { getStoredKey, isCryptoAvailable, wrapVaultKey, WRAPPED_KEY_MODES, wrapIdentifierFor } from "../lib/crypto";
import { toast } from "sonner";

const resetSchema = z.object({
  password: z
    .string()
    .min(8, "Senha deve ter pelo menos 8 caracteres")
    .regex(/[A-Z]/, "Senha deve conter pelo menos uma letra maiúscula")
    .regex(/[0-9]/, "Senha deve conter pelo menos um número"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const {
    user,
    validatePasswordRecoveryToken,
    completePasswordRecovery,
    logout,
    isLoading,
  } = useAuthStore();
  const [context, setContext] = useState(null);
  const [checking, setChecking] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetSchema),
  });

  useEffect(() => {
    let active = true;

    async function validate() {
      if (!token) {
        setContext({ valid: false });
        setChecking(false);
        return;
      }

      const result = await validatePasswordRecoveryToken(token);
      if (!active) return;
      setContext(result.success ? result.data : { valid: false });
      setChecking(false);
    }

    validate();
    return () => {
      active = false;
    };
  }, [token, validatePasswordRecoveryToken]);

  const vaultKey = getStoredKey();
  const isSameUserSession = user?.email?.toLowerCase() === context?.email?.toLowerCase();
  const canSubmit = context?.valid && WRAPPED_KEY_MODES.includes(context?.crypto_mode) && vaultKey && isSameUserSession;

  const onSubmit = async (data) => {
    if (!isCryptoAvailable()) {
      toast.error("Seu navegador não suporta criptografia. Use um navegador moderno.");
      return;
    }

    if (!canSubmit) {
      toast.error("Abra o cofre em uma sessão desbloqueada antes de redefinir a senha.");
      return;
    }

    try {
      const identity = { id: context.user_id, email: context.email };
      const wrappedKey = await wrapVaultKey(vaultKey, data.password, wrapIdentifierFor(context.crypto_mode, identity));
      const result = await completePasswordRecovery(token, data.password, wrappedKey);

      if (result.success) {
        await logout();
        toast.success(result.message);
        navigate("/login");
      } else {
        toast.error(result.error);
      }
    } catch {
      toast.error("Não foi possível proteger a chave do cofre com a nova senha.");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Shield className="w-10 h-10 text-secondary" />
          <span className="text-3xl font-bold tracking-tight">KawaFinance</span>
        </div>

        <h1 className="text-2xl font-semibold text-center mb-2">Redefinir senha</h1>
        <p className="text-muted-foreground text-center mb-8">
          A nova senha também precisa proteger a chave criptografada do cofre.
        </p>

        {checking ? (
          <p className="text-center text-muted-foreground">Validando link...</p>
        ) : !context?.valid ? (
          <div className="alert-banner alert-warning">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-medium">Link inválido ou expirado</p>
              <p className="text-xs mt-1 opacity-80">Solicite um novo link de recuperação.</p>
            </div>
          </div>
        ) : !canSubmit ? (
          <div className="alert-banner alert-warning">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-medium">Cofre não está desbloqueado</p>
              <p className="text-xs mt-1 opacity-80">
                Por segurança, a senha só pode ser redefinida quando a chave atual deste usuário estiver aberta neste navegador.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nova senha</Label>
              <PasswordInput
                id="password"
                placeholder="••••••••"
                showLockIcon
                {...register("password")}
                data-testid="reset-password"
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar nova senha</Label>
              <PasswordInput
                id="confirmPassword"
                placeholder="••••••••"
                showLockIcon
                {...register("confirmPassword")}
                data-testid="reset-confirm-password"
              />
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full btn-glow" disabled={isLoading} data-testid="reset-submit">
              {isLoading ? "Redefinindo..." : "Redefinir senha"}
            </Button>
          </form>
        )}

        <Link to="/login" className="mt-6 flex items-center justify-center gap-2 text-sm text-primary hover:underline">
          <ArrowLeft className="w-4 h-4" />
          Voltar para login
        </Link>
      </div>
    </div>
  );
}
